import React, { useCallback, useMemo, useRef } from 'react';

import { useEditorDoc, useEditorUi } from '../../EditorProvider';
import type { TraditionalDoc } from './doc';
import type { TraditionalUi } from './ui';
import { wordAt } from './words';
import { Cell } from './Cell';

// Grade SOMENTE LEITURA: a tradicional não edita letras nem posições, só as dicas.
// Clicar numa letra seleciona a horizontal que passa por ela; clicar de novo alterna
// para a vertical (quando há as duas). O texto da dica é digitado no Inspector.

export const Canvas: React.FC = () => {
  const doc = useEditorDoc<TraditionalDoc>();
  const [ui, setUi] = useEditorUi<TraditionalUi>();

  const stateRef = useRef({ doc, ui });
  stateRef.current = { doc, ui };

  const onCell = useCallback(
    (x: number, y: number) => {
      const { doc: d, ui: u } = stateRef.current;
      const { horizontal, vertical } = wordAt(d, x, y);
      const next =
        horizontal && vertical
          ? u.selectedKey === horizontal.key
            ? vertical
            : horizontal
          : horizontal ?? vertical;
      if (next) setUi((prev) => ({ ...prev, selectedKey: next.key }));
    },
    [setUi],
  );

  const { letters, highlighted, black } = useMemo(() => {
    const letters = new Map<string, string>();
    const highlighted = new Set<string>();
    for (const w of doc.palavras) {
      for (let i = 0; i < w.texto.length; i++) {
        const x = w.direcao ? w.x : w.x + i;
        const y = w.direcao ? w.y + i : w.y;
        letters.set(`${x},${y}`, w.texto[i]);
        if (w.key === ui.selectedKey) highlighted.add(`${x},${y}`);
      }
    }
    const black = new Set(doc.casasPretas.map((c) => `${c.x},${c.y}`));
    return { letters, highlighted, black };
  }, [doc.palavras, doc.casasPretas, ui.selectedKey]);

  const rows = Array.from({ length: doc.altura }, (_, y) => y);
  const cols = Array.from({ length: doc.largura }, (_, x) => x);

  return (
    <div role="grid" aria-label="Grade da cruzada" style={{ display: 'inline-block', lineHeight: 0 }}>
      {rows.map((y) => (
        <div key={y} role="row" style={{ display: 'flex' }}>
          {cols.map((x) => {
            const k = `${x},${y}`;
            return (
              <Cell
                key={x}
                x={x}
                y={y}
                black={black.has(k)}
                letter={letters.get(k) ?? ''}
                highlighted={highlighted.has(k)}
                onClick={onCell}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};
